import { ref, computed } from 'vue';
import { airflow } from '@aviata/platform-client';
import { flightEntity } from 'freedom-shared';
import type { Ref } from 'vue';

import * as offerUtils from '../lib';
import type { Result } from '../types';

export function useSearch(
  startSearch: (
    query: string,
    callback?: (search: Result['$meta']) => void,
  ) => Promise<Result>,
) {
  const isLoading = ref(true);
  const isError = ref(false);
  const result: Ref<Result> = ref({} as Result);
  const isFetching = ref(false);
  // Нужен, чтобы игнорировать ответы старых поисков (polling) и не затирать актуальные данные.
  let activeSearchId = 0;

  const groupedOffers = computed(() =>
    airflow.utils.groupOffers(result.value.items ?? []),
  );

  const sortedOffers = computed(() => {
    const grouped = groupedOffers.value;
    const { ranking } = result.value;

    if (ranking?.length) {
      return offerUtils.sortOffersByRanking(grouped, ranking);
    }
    return offerUtils.sortOffers(grouped);
  });

  const mappedOffers: Ref<flightEntity.types.MappedOffer[]> = computed(() => {
    if (result.value.$meta?.dict) {
      const { dict } = result.value.$meta;
      return sortedOffers.value.map(offer => ({
        ...offer,
        flights: offer.flights.map(flight =>
          flightEntity.lib.mapFlight(dict, flight),
        ),
      }));
    }
    return [];
  });

  const isNoResults = computed(() => result.value.items?.length === 0);

  function startAirSearch(query: string) {
    // Каждый новый поиск получает свой id; старые ответы будут проигнорированы.
    activeSearchId += 1;
    const searchId = activeSearchId;
    isError.value = false;
    isFetching.value = true;
    isLoading.value = true;
    return startSearch(query, search => {
      if (searchId !== activeSearchId) {
        return;
      }
      result.value = {
        $meta: search,
        id: '',
        status: 'processing' as Result['status'],
        items: [],
      };
      isFetching.value = false;
    })
      .then(res => {
        if (searchId !== activeSearchId) {
          return res;
        }
        result.value = {
          ...res,
          $meta: {
            ...(result.value.$meta ?? {}),
            ...(res.$meta ?? {}),
          },
        };
        return res;
      })
      .catch(error => {
        if (searchId === activeSearchId) {
          isError.value = true;
        }
        throw error;
      })
      .finally(() => {
        if (searchId !== activeSearchId) {
          return;
        }
        isLoading.value = false;
        isFetching.value = false;
      });
  }

  return {
    offers: mappedOffers,
    result,
    isNoResults,
    isLoading,
    startAirSearch,
    isError,
    isFetching,
  };
}
